import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { EyeCloseIcon, EyeOpenIcon, VerifiedTick } from "../../assets/assets";
import inputErrorIcon from "../../assets/inputError.svg";
import inputValidIcon from "../../assets/inputValid.svg";
import "../../design-system.css";
import { isPureNumber } from "../../services/validators";
import { Regex } from "../../utils/constants";
import "./InputBox.css";

function InputBox(props) {
  const [value, setValue] = useState("");
  const [error, setError] = useState();
  const label = useRef(null);
  const inputRef = useRef(null);
  const previousLoadingState = useRef(false);
  const [complete, setComplete] = useState(false);

  const [visibleMap, setVisibleMap] = useState({});

  const toggleVisibility = () => {
    setVisibleMap((prev) => ({
      ...prev,
      [props.name]: !prev[props.name]
    }));
  };

  //set complete status
  useEffect(() => {
    if (previousLoadingState.current === true && props.loading === false) {
      setComplete(true);
    } else if (complete) {
      setComplete(false);
    }
    previousLoadingState.current = props.loading;
    // eslint-disable-next-line
  }, [props.loading]);

  const handleTextInput = (event) => {
    setValue(event.target.value);
    if (typeof props.onChange === "function") {
      props.onChange(event);
    }
  };

  // set onchange value
  useEffect(() => {
    setValue(props.value);
    setComplete(false);
  }, [props.value]);

  const validators = (value) => {
    let isValidInput = true;

    const trimmed = value?.toString().trim() ?? "";

    const setFieldError = (msg) => {
      isValidInput = false;
      if (error !== msg) setError(msg);
    };

    const clearError = () => {
      if (error) setError(null);
    };

    // empty value guard
    if (!value || trimmed.length === 0) {
      isValidInput = false;
      props.isValidInput?.(isValidInput);
      // inputRef.current.classList.add("error");
      return;
    }

    const emailValidate = /^[a-zA-Z0-9.]+@(?:[a-zA-Z0-9]+\.)+[A-Za-z]+$/;
    const userNameValidate = /^[a-zA-Z. ]+$/;
    const phoneNumberValidate = /^[0-9+]+$/;

    /* ---------- custom validation ---------- */
    if (props.validation) {
      const result = props.validation(value);
      result?.status === false ? setFieldError(result.message) : clearError();
    } else if (
      /* ---------- year validation ---------- */
      props.name?.toLowerCase().includes("year") &&
      props.name !== "yearOfExperience"
    ) {
      if (trimmed.length === 4 && isPureNumber(trimmed)) {
        clearError();
      } else {
        setFieldError("Please enter a valid Year");
      }
    } else {
      /* ---------- field-based validation ---------- */
      switch (props.name) {
        case "phoneNumber": {
          const len = trimmed.length;

          if (len <= 3) {
            clearError();
            props.isValidInput?.(false);
            inputRef.current.classList.remove("error");
            break;
          }

          const numberAfterCode = trimmed.slice(3);

          const isInvalid =
            !trimmed.startsWith("+91") ||
            !/^\d+$/.test(numberAfterCode) ||
            numberAfterCode.length !== 10 ||
            !phoneNumberValidate.test(numberAfterCode);

          if (isInvalid) {
            setFieldError("Please enter a valid phone number");
            props.isValidInput?.(false);
            inputRef.current.classList.add("error");
            return;
          }

          clearError();
          break;
        }

        case "fullName":
        case "userName":
          if (
            trimmed.length <= 1 ||
            !isNaN(trimmed) ||
            !userNameValidate.test(trimmed)
          ) {
            setFieldError("Please enter a valid name");
          } else {
            clearError();
          }
          break;

        case "email":
          if (!Regex.email.test(trimmed) || !emailValidate.test(trimmed)) {
            setFieldError("Please enter a valid Email");
          } else {
            clearError();
          }
          break;

        case "aadhaarNumber":
          if (trimmed.length <= 1 || !Regex.aadhaarNumber.test(trimmed)) {
            setFieldError("Please enter a valid aadhaar number");
          } else {
            clearError();
          }
          break;

        case "pincode":
          if (!isPureNumber(trimmed) || !/^[1-9][0-9]{5}$/.test(trimmed)) {
            setFieldError("Please enter a valid pincode");
          } else {
            clearError();
          }
          break;

        case "date": {
          const year = parseInt(trimmed);
          const currentYear = new Date().getFullYear();
          if (year < currentYear - 100 || year > currentYear) {
            setFieldError("Please enter a valid date");
          } else {
            clearError();
          }
          break;
        }

        case "time": {
          const [h, m] = trimmed.split(":").map(Number);
          const now = new Date();
          if (h > now.getHours() || m > now.getMinutes()) {
            setFieldError("Please enter a valid time");
          } else {
            clearError();
          }
          break;
        }

        case "height":
          if (isPureNumber(trimmed) && +trimmed >= 30 && +trimmed <= 270) {
            clearError();
          } else {
            setFieldError("Please enter a valid height");
          }
          break;

        case "bodyTemperature":
          if (isPureNumber(trimmed) && +trimmed <= 200) {
            clearError();
          } else {
            setFieldError("Please enter a valid body temperature");
          }
          break;

        case "bloodOxygen":
          if (isPureNumber(trimmed) && +trimmed > 0 && +trimmed <= 100) {
            clearError();
          } else {
            setFieldError("Please enter a valid blood oxygen");
          }
          break;

        case "bloodSugar":
          if (isPureNumber(trimmed) && +trimmed >= 50 && +trimmed <= 999) {
            clearError();
          } else {
            setFieldError("Please enter a valid blood sugar");
          }
          break;

        case "heartRate":
          if (isPureNumber(trimmed) && +trimmed >= 0 && +trimmed <= 270) {
            clearError();
          } else {
            setFieldError("Please enter a valid heart rate");
          }
          break;

        case "weight":
          if (isPureNumber(trimmed) && +trimmed > 2 && +trimmed <= 635) {
            clearError();
          } else {
            setFieldError("Please enter a valid weight");
          }
          break;

        default:
          clearError();
      }
    }

    /* ---------- final sync ---------- */
    props.isValidInput?.(isValidInput);

    inputRef.current.classList.toggle("error", !isValidInput);
  };

  //for validation
  useEffect(() => {
    validators(value);
    // eslint-disable-next-line
  }, [value, props.validation]);

  return (
    <div className={`inherit-parent-width  ${props.className || ""}`}>
      <div
        className={` position-relative display-flex flex-align-items-end height-48px inherit-parent-width  ${
          props.disabled === true ? " background-color-grey " : ""
        } ${
          props.size === "half"
            ? "min-width-138px"
            : props.size === "small"
            ? "min-width-120px"
            : "min-width-250px"
        }`}
      >
        <div className="flex-center-children-horizontally font-size-medium font-family-gilroy-medium inherit-parent-width">
          <div className="inherit-parent-width">
            <input
              onKeyUp={props.onKeyUp}
              id={props.name}
              data-cy={props?.["data-cy"] || `${props.name}-input-box`}
              readOnly={props.disabled}
              ref={inputRef}
              autoFocus={props.autoFocus}
              // defaultValue={props.defaultValue}
              value={props.value}
              className={`input-box background-transparent max-width-100-percentage inherit-parent-width font-family-gilroy-regular font-size-medium padding-top-large padding-bottom-default display-block  input-box-border-bottom-default input-box-disabled-padding-left-5px border-radius-0 ${
                props.buttonTile ? "padding-right-4p7-em" : ""
              } ${(props.type === "date" || props.type === "time") &&
                "padding-right-0"} ${
                props.disabled === true
                  ? " font-color-secondary disabled"
                  : " font-color-secondary"
              }`}
              autoComplete={props.autoComplete}
              min={props.minDate}
              max={
                props.type === "date"
                  ? props.maxDate
                    ? props.maxDate
                    : new Date().toISOString().split("T")[0]
                  : null
              }
              onFocus={(_event) => {
                if (props.disabled === true) return;

                if (label.current) {
                  label.current?.classList.add("active");
                }
                if (inputRef.current) {
                  inputRef.current?.classList.add("active");
                }
              }}
              onClick={(event) => {
                if (props.type === "date" || props.type === "time") {
                  if (window.Cypress) {
                    inputRef.current.click();
                  } else {
                    event.target.showPicker?.();
                  }
                }
              }}
              onBlur={(event) => {
                if (
                  !event.currentTarget.value &&
                  props.type !== "date" &&
                  props.type !== "time"
                )
                  label.current?.classList.remove("active");
                inputRef.current?.classList.remove("active");
                if (event.target.value.trim().length === 0) {
                  setError(null);
                  inputRef.current.classList.remove("error");
                }
                if (typeof props.onOutOfFocus === "function") {
                  props.onOutOfFocus(event);
                }
              }}
              type={
                props.type === "password"
                  ? "text"
                  : props.type === "number"
                  ? "text"
                  : props.type || "text"
              }
              style={
                props.type === "password"
                  ? {
                      WebkitTextSecurity: visibleMap[props.name]
                        ? "none"
                        : "circle"
                    }
                  : {}
              }
              name={props.name}
              onChange={handleTextInput}
              required={props.required === true}
              inputMode={
                props.type === "number"
                  ? "numeric"
                  : props.inputMode
                  ? props.inputMode
                  : null
              }
            />
            <span className="input-box-border-bottom" />
            <label
              htmlFor={props.name}
              ref={label}
              className={` ${
                props.removeResponsive ? "" : "adaptive-font-size"
              } input-box-label  font-color-secondary  position-absolute font-family-gilroy-medium font-size-medium ${props.labelClassName ||
                ""} ${
                inputRef.current === document.activeElement ||
                props.value ||
                props.defaultValue ||
                (props.type === "number" && parseInt(props.value) > 0) ||
                props.type === "time" ||
                props.type === "date" ||
                props.alwaysActive ||
                props.autoFocus === true
                  ? "active"
                  : ""
              } ${props.disabled ? " disabled " : ""}
							`}
            >
              {props.label}
            </label>
            {typeof props.inputSuffixPlaceHolder === "string" && (
              <div className="position-absolute right-0 bottom-0 padding-bottom-default display-flex flex-align-items-center">
                <label
                  className={` font-color-secondary font-size-small font-family-gilroy-regular padding-left-small `}
                >
                  {props.inputSuffixPlaceHolder}
                </label>
              </div>
            )}
          </div>
          <div
            className={`position-absolute display-flex flex-align-items-center flex-justify-content-center min-width-1em absolute-center-self-vertically
					 	${props.type === "date" || props.type === "time" ? " right-1p5em" : " right-0"}
					 `}
          >
            <div className="suffix-slot">
              {typeof props.inputSuffixPlaceHolder === "string" ? null : (
                <InputSuffixIcon {...props} error={error} complete={complete} />
              )}
            </div>

            <div className="suffix-slot">
              {props.type === "password" && (
                <TogglePasswordVisibility
                  visible={!!visibleMap[props.name]}
                  error={error}
                  onToggle={toggleVisibility}
                />
              )}
            </div>

            <div className="suffix-slot">
              {props.buttonTile && (
                <button
                  id={props.buttonTileId}
                  type={props.buttonType}
                  onClick={props.onButtonClick}
                  disabled={props.suffixButtonDisabled}
                  style={{
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    paddingTop: "6px",
                    paddingBottom: "6px"
                  }}
                  className={`${
                    props.buttonClassName
                  } ${props.buttonFontColor ||
                    "font-color-tertiaryColor6"} font-weight-normal margin-left-medium margin-right-medium  font-size-smaller background-transparent margin-left-8px suffix-button-border border-radius-default text-transform-uppercase`}
                >
                  {props.buttonTile}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <InputInfo {...props} error={error} />
    </div>
  );
}

const InputSuffixIcon = ({
  loading,
  name,
  setVerified,
  iconClassNames,
  error,
  value,
  complete
}) => {
  if (loading === true) {
    return (
      <div
        className="circular-loader"
        data-cy={`${name}-input-box-loader-icon`}
      />
    );
  } else if (setVerified) {
    return <VerifiedTick />;
  } else if (typeof error === "string") {
    return (
      <img
        className={`height-width-1em ${iconClassNames}`}
        src={inputErrorIcon}
        alt="inputErrorIcon"
        data-cy={`${name}-input-box-error-icon`}
      />
    );
  } else if (value && `${value}`.trim().length !== 0 && complete === true) {
    return (
      <img
        className={`height-width-1em ${iconClassNames} cursor-pointer`}
        src={inputValidIcon}
        alt="inputValidIcon"
        data-cy={`${name}-input-box-complete-icon`}
      />
    );
  } else {
    return null;
  }
};

const InputInfo = ({ notes, inputNotesClassName, error }) => {
  if (typeof error === "string" && error !== "") {
    return (
      <p className="font-size-smaller padding-top-small font-color-red">
        {error}
      </p>
    );
  } else if (notes) {
    return (
      <div
        className={` ${inputNotesClassName ||
          "font-size-smaller font-color-secondary padding-top-small"} `}
      >
        {notes}
      </div>
    );
  } else {
    return <p className="font-size-smaller">&nbsp;</p>;
  }
};

InputBox.propTypes = {
  /**
   * prop which shows loading spinner animation
   */
  loading: PropTypes.bool,
  /**
   * prop which return input onChange event as callback
   */
  onChange: PropTypes.func,
  /**
   * input element value
   */
  value: PropTypes.string,
  /**
   * callback function which returns the input is valid or not
   * `props.isValidInput(isValidInput)` returns `true` or `false`
   */
  isValidInput: PropTypes.func,
  /**
   * custom validation function
   */
  validation: PropTypes.func,
  /**
   * name attribute of input element
   */
  name: PropTypes.string,
  /**
   * class name for icons
   */
  iconClassNames: PropTypes.string,
  /**
   * notes
   */
  notes: PropTypes.string,
  /**
   * class name for parent element of the input component
   */
  className: PropTypes.string,
  /**
   * which is used to disable the input element
   */
  disabled: PropTypes.bool,
  /**
   * which is used to remove the adaptiveness of font size
   */
  removeResponsive: PropTypes.bool,
  /**
   * to control the min-width of the component
   */
  size: PropTypes.oneOf(["half", "small"]),
  /**
   * selector attribute for cypress testing, by default value is `-input-box` followed by the input name
   */
  "data-cy": PropTypes.string,
  /**
   * event callback prop
   */
  autoFocus: PropTypes.bool,
  /**
   * input element attribute which shows default value
   */
  defaultValue: PropTypes.string,
  /**
   * additional button which will reside inside the input component
   */
  buttonTile: PropTypes.string,
  /**
   * input type
   * `date` `time` `text` `number` `tel`
   */
  type: PropTypes.string,
  /**
   * attribute used to enable auto complete
   */
  autoComplete: PropTypes.oneOf(["on", "off"]),
  /**
   * for input `type="date"`
   */
  minDate: PropTypes.string,
  /**
   * onBlur event of the input element
   */
  onOutOfFocus: PropTypes.func,
  /**
   * which defines the input is required or not
   */
  required: PropTypes.bool,
  /**
   * class name label element
   */
  labelClassName: PropTypes.string,
  /**
   * property which sets the label as active state
   */
  alwaysActive: PropTypes.bool,
  /**
   * label for the input box component
   */
  label: PropTypes.string,
  /**
   * id for addition button tile
   */
  buttonTileId: PropTypes.string,
  /**
   * type of button tile
   */
  buttonType: PropTypes.oneOf(["submit", "button", "reset"]),
  /**
   * button tile onClick call back function
   */
  onButtonClick: PropTypes.func,
  /**
   * disable state for button tile
   */
  suffixButtonDisabled: PropTypes.bool
};

InputBox.defaultProps = {
  loading: false,
  removeResponsive: false,
  onChange: undefined,
  value: undefined,
  isValidInput: undefined,
  validation: undefined,
  name: "default",
  iconClassNames: "",
  notes: null,
  className: "",
  disabled: false,
  size: null,
  "data-cy": null,
  autoFocus: null,
  buttonTile: null,
  type: null,
  autoComplete: "off",
  onOutOfFocus: undefined,
  required: false,
  labelClassName: "",
  alwaysActive: false,
  label: null,
  buttonTileId: null,
  buttonType: "button",
  onButtonClick: undefined,
  suffixButtonDisabled: false
};

export default InputBox;

const TogglePasswordVisibility = ({ visible, onToggle, error }) => {
  return (
    <div className={`padding-left-default`} onClick={onToggle}>
      {visible ? <EyeOpenIcon /> : <EyeCloseIcon />}
    </div>
  );
};
